/**
 * OpenPositions — terminal-style table of every position currently open.
 *
 * Polls /positions/open every 5s; PnL colour flips green/rose on
 * sign. DCA level rendered as a small fire badge so it pops at a glance.
 */
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import { formatNumber, formatPct, formatUsd, timeAgo } from '@/lib/format'
import type { OpenPosition } from '@/hooks/useDashboardApi'

interface OpenPositionsProps {
  items: OpenPosition[] | undefined
  loading?: boolean
}

export function OpenPositions({ items, loading }: OpenPositionsProps) {
  const { t } = useTranslation('app')

  return (
    <section className="bg-ink">
      <header className="flex items-baseline px-5 py-3 border-b border-line">
        <h3 className="text-sm font-semibold">
          {t('dashboard.open_positions.title')}
        </h3>
        <span className="ml-2 text-xs text-textFaint font-mono">
          {items ? items.length : '—'}
        </span>
      </header>

      {loading || items === undefined ? (
        <div className="px-5 py-10 text-center text-textFaint text-sm">—</div>
      ) : items.length === 0 ? (
        <div className="px-5 py-10 text-center text-textFaint text-sm">
          {t('dashboard.open_positions.empty')}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-textDim text-xs uppercase tracking-wider border-b border-line bg-elevated/40">
              <tr>
                <th className="text-left px-5 py-2 font-medium">{t('dashboard.open_positions.table.symbol')}</th>
                <th className="text-left py-2 font-medium">{t('dashboard.open_positions.table.side')}</th>
                <th className="text-right py-2 font-medium">{t('dashboard.open_positions.table.size')}</th>
                <th className="text-right py-2 font-medium">{t('dashboard.open_positions.table.entry')}</th>
                <th className="text-right py-2 font-medium">{t('dashboard.open_positions.table.mark')}</th>
                <th className="text-right py-2 font-medium">{t('dashboard.open_positions.table.pnl')}</th>
                <th className="text-right py-2 font-medium">{t('dashboard.open_positions.table.age')}</th>
                <th className="text-right px-5 py-2 font-medium">{t('dashboard.open_positions.table.dca')}</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {items.map((p) => <PositionRow key={p.id} pos={p} />)}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function PositionRow({ pos }: { pos: OpenPosition }) {
  const { t } = useTranslation('app')
  const pnl = pos.unrealized_pnl
  const pct = pos.unrealized_pnl_pct
  const pnlTone = pnl == null
    ? 'text-textFaint'
    : pnl >= 0 ? 'text-pulse' : 'text-rose'
  return (
    <tr className="border-b border-line/40 hover:bg-elevated/30 transition-colors">
      <td className="px-5 py-2 font-semibold">{pos.symbol}</td>
      <td className="py-2">
        <span className={cn(
          'pill',
          pos.direction === 'long' ? 'bg-pulse-faint text-pulse' : 'bg-rose-faint text-rose',
        )}>
          {t(`dashboard.open_positions.side.${pos.direction}`)}
        </span>
      </td>
      <td className="text-right py-2">{formatNumber(pos.quantity, 4)}</td>
      <td className="text-right py-2 text-textDim">{formatNumber(pos.entry_price, 4)}</td>
      <td className="text-right py-2">
        {pos.current_price != null ? formatNumber(pos.current_price, 4) : '—'}
      </td>
      <td className={cn('text-right py-2', pnlTone)}>
        {pnl != null ? formatUsd(pnl, { sign: true }) : '—'}
        {pct != null && (
          <span className="ml-1 text-textFaint">
            {formatPct(pct, { sign: true })}
          </span>
        )}
      </td>
      <td className="text-right py-2 text-textFaint">{timeAgo(pos.opened_at)}</td>
      <td className="text-right px-5 py-2">
        {pos.dca_level > 0 ? (
          <span className="pill bg-fire-faint text-fire">DCA{pos.dca_level}</span>
        ) : (
          <span className="text-textFaint">—</span>
        )}
      </td>
    </tr>
  )
}
